import React, { useEffect } from "react";
import { SYSTEMS, type SystemMeta } from "../../config/systems";
import { buildSystemUrl } from "../../config/domain";
import { applyTheme, getCurrentTheme } from "../../themes/themeConfig";
import "../../themes/HomePage.themed.css";

/**
 * Unified portal landing page (Global Operations Hub).
 *
 * Reuses the MSA HomePage styling (hero + Platform Overview cards) but without
 * the left navigation menu. The Overview area shows an intro card per system
 * (MSA / Atlas); clicking one does a full-page redirect to that system's
 * subdomain. The card list is derived from the single SYSTEMS source of truth —
 * adding a system only requires editing systems.ts.
 */
const Portal: React.FC = () => {
  const systems: SystemMeta[] = Object.values(SYSTEMS);

  // Apply the current theme so the hero/overview pick up --theme-* CSS
  // variables (heroBackground, mainBackground, sectionBackground, …). Without
  // this the portal renders on a bare white background: in local-dev preview
  // the App-level auth bootstrap (which normally calls applyTheme) is skipped,
  // and even in prod the portal branch renders outside AppContent's theme
  // effect. Re-apply on "themeChanged" so the settings selector updates it live.
  useEffect(() => {
    const update = () => applyTheme(getCurrentTheme());
    update();
    window.addEventListener("themeChanged", update);
    return () => window.removeEventListener("themeChanged", update);
  }, []);

  const handleOpen = (system: SystemMeta) => {
    // Full-page redirect to the target system's subdomain (in local dev this
    // switches the ?system= query param instead).
    window.location.href = buildSystemUrl(system.subdomain);
  };

  return (
    <div className="msa-home portal-home">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div>
            <div>
              <h1 className="hero-title">GOH</h1>
              <div className="hero-subtitle">Global Operations Hub</div>
            </div>
            <div className="hero-logo">
              <img
                src={require("../../assets/images/project-logo.png")}
                alt="Global Operations Hub Logo"
              />
            </div>
          </div>
          <p className="hero-description">
            One entry point for Global Operations systems. Select a system to
            continue — your access within each system is controlled by
            permissions.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="main-content">
        <section className="content-section">
          <h3>Systems Overview</h3>
          <div className="platform-overview">
            <p>
              The Global Operations Hub unifies multiple operations systems
              behind a single sign-on. Each system below shares the same
              platform; permissions determine which capabilities you see once
              you enter.
            </p>
            <div className="platform-features">
              <div className="features-row">
                {systems.map((system) => (
                  <div
                    key={system.key}
                    className="feature-item"
                    role="link"
                    tabIndex={0}
                    onClick={() => handleOpen(system)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleOpen(system);
                      }
                    }}
                    data-testid={`portal-enter-${system.subdomain}`}
                  >
                    <div className="feature-icon">{system.icon ?? "🔗"}</div>
                    <h4>{system.label}</h4>
                    <p>{system.overview ?? system.description ?? ""}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Portal;
